import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InputField, ComboboxInputField } from "@/components/shared";
import { useTranslation } from "react-i18next";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { provincesSimplified } from "@/constants/provinces";
import { PlusIcon, TrashIcon } from "lucide-react";

/**
 * Props for the ChildOrganizationForm component
 */
interface ChildOrganizationFormProps {
  form: UseFormReturn<any>;
  fieldName: string;
  level?: number;
  onAddChild?: (_index: number) => void;
  onRemove?: () => void;
  canRemove?: boolean;
}

/**
 * Component for managing a single child organization with nested children support.
 * Supports unlimited nesting levels with proper indentation and styling.
 *
 * @component
 * @example
 * <ChildOrganizationForm
 *   form={form}
 *   fieldName="children.0"
 *   level={0}
 *   onAddChild={(index) => addChildOrganization(index)}
 *   onRemove={() => removeChildOrganization(0)}
 *   canRemove={true}
 * />
 */
export function ChildOrganizationForm({
  form,
  fieldName,
  level = 0,
  onAddChild: _onAddChild,
  onRemove,
  canRemove = true
}: ChildOrganizationFormProps) {
  const { t } = useTranslation(["modals"]);

  const { fields: childrenFields, append: appendChild, remove: removeChild } = useFieldArray({
    control: form.control,
    name: `${fieldName}.children`
  });

  const handleAddChild = () => {
    appendChild({
      organization: {
        name: "",
        province: "",
        shortName: ""
      },
      parentId: null,
      children: undefined // Optional in CreateOrganizationsInput
    });
  };

  const handleRemoveChild = (index: number) => {
    removeChild(index);
  };

  return (
    <div className="w-full">
      <Card className="w-full">
        <CardHeader className="p-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {level === 0 ? t("createorganization.childorganization") : `${t("createorganization.suborganization")} (Level ${level + 1})`}
            </CardTitle>
            {canRemove && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRemove}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4 px-3 pb-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <InputField
              label={t("createorganization.inputs.name.label")}
              name={`${fieldName}.organization.name`}
              placeholder={t("createorganization.inputs.name.placeholder")}
              form={form}
              className="w-full"
            />

            <InputField
              label={t("createorganization.inputs.shortname.label")}
              name={`${fieldName}.organization.shortName`}
              placeholder={t("createorganization.inputs.shortname.placeholder")}
              form={form}
              className="w-full"
            />

            <ComboboxInputField
              form={form}
              name={`${fieldName}.organization.province`}
              label={t("createorganization.inputs.province.label")}
              options={provincesSimplified.map((province) => ({
                label: province.name,
                value: province.code
              }))}
              className="w-full"
            />
          </div>

          {/* Children Organizations */}
          {childrenFields.length > 0 && (
            <div className="space-y-4 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">
                  {t("createorganization.suborganizations")} ({childrenFields.length})
                </h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddChild}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  {t("createorganization.addsuborganization")}
                </Button>
              </div>

              <div className="space-y-4 min-w-0">
                {childrenFields.map((_, index) => (
                  <ChildOrganizationForm
                    key={`${fieldName}.children.${index}`}
                    form={form}
                    fieldName={`${fieldName}.children.${index}`}
                    level={level + 1}
                    onRemove={() => handleRemoveChild(index)}
                    canRemove={true}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Add Child Button - only show if no children exist */}
          {childrenFields.length === 0 && (
            <div className="flex justify-center pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddChild}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                {t("createorganization.addsuborganization")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
